// ============================================================================
// BACKORDER WORKFLOW COMPONENT
// UI component for handling backorders when stock is insufficient
// Date: June 2026
// ============================================================================

'use client';

import { useState } from 'react';
import { AlertTriangle, Package, Calendar, Check, X } from 'lucide-react';
import { toast } from 'react-hot-toast';
import type { StockAvailabilityCheck } from '@/lib/inventory/stockReservationService';

interface BackorderItem {
  work_item_id: string;
  item_name?: string;
  item_code?: string;
  requested_quantity: number;
  available_quantity: number;
  shortage: number;
  backorder_quantity: number;
  expected_date?: string;
}

interface BackorderWorkflowProps {
  insufficientItems: Array<{
    work_item_id: string;
    item_name?: string;
    item_code?: string;
    availability: StockAvailabilityCheck;
  }>;
  onConfirm: (backorderItems: BackorderItem[]) => Promise<void>;
  onCancel: () => void;
  isDarkMode?: boolean;
}

export function BackorderWorkflow({
  insufficientItems,
  onConfirm,
  onCancel,
  isDarkMode = false,
}: BackorderWorkflowProps) {
  const [backorderItems, setBackorderItems] = useState<BackorderItem[]>(
    insufficientItems.map(item => ({
      work_item_id: item.work_item_id,
      item_name: item.item_name,
      item_code: item.item_code,
      requested_quantity: item.availability.requested_quantity,
      available_quantity: item.availability.quantity_available,
      shortage: item.availability.shortage,
      backorder_quantity: item.availability.shortage,
      expected_date: '',
    }))
  );
  const [isProcessing, setIsProcessing] = useState(false);
  const [step, setStep] = useState<'review' | 'confirm'>('review');

  const totalShortage = backorderItems.reduce((sum, item) => sum + item.shortage, 0);
  const totalBackorder = backorderItems.reduce((sum, item) => sum + item.backorder_quantity, 0);

  const handleQuantityChange = (workItemId: string, quantity: number) => {
    setBackorderItems(prev =>
      prev.map(item =>
        item.work_item_id === workItemId
          ? { ...item, backorder_quantity: Math.max(0, Math.min(quantity, item.shortage)) }
          : item
      )
    );
  };

  const handleDateChange = (workItemId: string, date: string) => {
    setBackorderItems(prev =>
      prev.map(item =>
        item.work_item_id === workItemId ? { ...item, expected_date: date } : item
      )
    );
  };

  const handleConfirm = async () => {
    setIsProcessing(true);
    try {
      await onConfirm(backorderItems.filter(item => item.backorder_quantity > 0));
      toast.success('Backorder created successfully');
    } catch (error) {
      console.error('Failed to create backorder:', error);
      toast.error('Failed to create backorder');
    } finally {
      setIsProcessing(false);
    }
  };

  const canProceed = backorderItems.some(item => item.backorder_quantity > 0);

  return (
    <div className={`fixed inset-0 bg-black/50 flex items-center justify-center z-[150] p-4`}>
      <div className={`max-w-2xl w-full max-h-[90vh] overflow-y-auto rounded-xl shadow-2xl ${isDarkMode ? 'bg-gray-800' : 'bg-white'}`}>
        {/* Header */}
        <div className={`p-6 border-b ${isDarkMode ? 'border-gray-700' : 'border-gray-200'}`}>
          <div className="flex items-center gap-3">
            <div className={`w-10 h-10 rounded-full flex items-center justify-center ${isDarkMode ? 'bg-amber-900/30' : 'bg-amber-100'}`}>
              <AlertTriangle className={`w-5 h-5 ${isDarkMode ? 'text-amber-400' : 'text-amber-600'}`} />
            </div>
            <div>
              <h2 className={`text-lg font-semibold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                Insufficient Stock - Backorder Required
              </h2>
              <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                {backorderItems.length} item{backorderItems.length !== 1 ? 's' : ''} cannot be fulfilled from current stock
              </p>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="p-6">
          {step === 'review' ? (
            <>
              <div className={`mb-4 p-4 rounded-lg ${isDarkMode ? 'bg-amber-900/20 border border-amber-700' : 'bg-amber-50 border border-amber-200'}`}>
                <p className={`text-sm ${isDarkMode ? 'text-amber-200' : 'text-amber-800'}`}>
                  <strong>Backorder Option:</strong> You can place the unavailable items on backorder. 
                  These will be automatically fulfilled when stock becomes available.
                </p>
              </div>

              <div className="space-y-4">
                {backorderItems.map(item => (
                  <div
                    key={item.work_item_id}
                    className={`p-4 rounded-lg border ${isDarkMode ? 'bg-gray-700/50 border-gray-600' : 'bg-gray-50 border-gray-200'}`}
                  >
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex-1">
                        <div className={`font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                          {item.item_name || item.item_code || item.work_item_id}
                        </div>
                        <div className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                          Requested: {item.requested_quantity} | Available: {item.available_quantity} | Shortage: {item.shortage}
                        </div>
                      </div>
                      <div className={`flex items-center gap-2 ${isDarkMode ? 'text-amber-400' : 'text-amber-600'}`}>
                        <Package className="w-4 h-4" />
                        <span className="font-semibold">{item.shortage}</span>
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className={`block text-xs font-medium mb-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                          Backorder Quantity
                        </label>
                        <input
                          type="number"
                          min="0"
                          max={item.shortage}
                          value={item.backorder_quantity}
                          onChange={(e) => handleQuantityChange(item.work_item_id, Number(e.target.value))}
                          className={`w-full px-3 py-2 rounded-lg border text-sm ${isDarkMode
                            ? 'bg-gray-700 border-gray-600 text-white'
                            : 'bg-white border-gray-300 text-gray-900'
                          }`}
                        />
                      </div>
                      <div>
                        <label className={`block text-xs font-medium mb-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                          Expected Date (Optional)
                        </label>
                        <input
                          type="date"
                          value={item.expected_date || ''}
                          onChange={(e) => handleDateChange(item.work_item_id, e.target.value)}
                          className={`w-full px-3 py-2 rounded-lg border text-sm ${isDarkMode
                            ? 'bg-gray-700 border-gray-600 text-white'
                            : 'bg-white border-gray-300 text-gray-900'
                          }`}
                        />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </>
          ) : (
            <div className="space-y-4">
              <div className={`p-4 rounded-lg ${isDarkMode ? 'bg-blue-900/20 border border-blue-700' : 'bg-blue-50 border border-blue-200'}`}>
                <h3 className={`font-semibold mb-2 ${isDarkMode ? 'text-blue-200' : 'text-blue-800'}`}>
                  Backorder Summary
                </h3>
                <div className={`text-sm ${isDarkMode ? 'text-blue-100' : 'text-blue-700'}`}>
                  <div className="flex justify-between mb-1">
                    <span>Total Items on Backorder:</span>
                    <span className="font-semibold">{backorderItems.filter(i => i.backorder_quantity > 0).length}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Total Quantity:</span>
                    <span className="font-semibold">{totalBackorder}</span>
                  </div>
                </div>
              </div>

              <div className="space-y-2">
                {backorderItems.filter(item => item.backorder_quantity > 0).map(item => (
                  <div
                    key={item.work_item_id}
                    className={`flex items-center justify-between p-3 rounded-lg ${isDarkMode ? 'bg-gray-700/50' : 'bg-gray-50'}`}
                  >
                    <div>
                      <div className={`font-medium text-sm ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                        {item.item_name || item.item_code || item.work_item_id}
                      </div>
                      {item.expected_date && (
                        <div className={`text-xs flex items-center gap-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                          <Calendar className="w-3 h-3" />
                          Expected: {item.expected_date}
                        </div>
                      )}
                    </div>
                    <div className={`font-semibold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                      {item.backorder_quantity}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className={`p-6 border-t ${isDarkMode ? 'border-gray-700' : 'border-gray-200'} flex justify-between`}>
          {step === 'review' ? (
            <>
              <button
                onClick={onCancel}
                disabled={isProcessing}
                className={`px-4 py-2 rounded-lg font-medium transition-colors flex items-center gap-2 ${isDarkMode
                  ? 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                } ${isProcessing ? 'opacity-50 cursor-not-allowed' : ''}`}
              >
                <X className="w-4 h-4" />
                Cancel
              </button>
              <div className="flex gap-3">
                <button
                  onClick={() => setStep('confirm')}
                  disabled={!canProceed || isProcessing}
                  className={`px-4 py-2 rounded-lg font-medium transition-colors flex items-center gap-2 ${isDarkMode
                    ? 'bg-blue-600 text-white hover:bg-blue-500'
                    : 'bg-blue-600 text-white hover:bg-blue-700'
                  } ${(!canProceed || isProcessing) ? 'opacity-50 cursor-not-allowed' : ''}`}
                >
                  Review Backorder
                </button>
              </div>
            </>
          ) : (
            <>
              <button
                onClick={() => setStep('review')}
                disabled={isProcessing}
                className={`px-4 py-2 rounded-lg font-medium transition-colors ${isDarkMode
                  ? 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                } ${isProcessing ? 'opacity-50 cursor-not-allowed' : ''}`}
              >
                Back
              </button>
              <div className="flex gap-3">
                <button
                  onClick={handleConfirm}
                  disabled={isProcessing}
                  className={`px-4 py-2 rounded-lg font-medium transition-colors flex items-center gap-2 ${isDarkMode
                    ? 'bg-green-600 text-white hover:bg-green-500'
                    : 'bg-green-600 text-white hover:bg-green-700'
                  } ${isProcessing ? 'opacity-50 cursor-not-allowed' : ''}`}
                >
                  {isProcessing ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      Processing...
                    </>
                  ) : (
                    <>
                      <Check className="w-4 h-4" />
                      Confirm Backorder
                    </>
                  )}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}